import "@/index.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";
import { WebSocketProvider } from "@/contexts/WebSocketContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { api } from "@/lib/api";
import LoginPage from "@/pages/LoginPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import DiscoveryPage from "@/pages/DiscoveryPage";
import UsersPage from "@/pages/UsersPage";
import CategoriesPage from "@/pages/CategoriesPage";

const LIGHT_BG = "https://images.unsplash.com/photo-1770954036265-c8e5966a7796?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxsaWdodCUyMGFic3RyYWN0JTIwbW9kZXJuJTIwZmx1aWQlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3NzgyMDY2N3ww&ixlib=rb-4.1.0&q=85";
const DARK_BG = "https://images.unsplash.com/photo-1761078739072-a10f63f3e478?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYWJzdHJhY3QlMjBtZXNoJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3Nzc4MjA2NjR8MA&ixlib=rb-4.1.0&q=85";

function MeshBackground({ url }) {
    return <div className="mesh-bg" style={{ backgroundImage: `url(${url})` }} />;
}

function AppContent() {
    const { theme } = useTheme();
    const [backgroundUrl, setBackgroundUrl] = useState(theme === "dark" ? DARK_BG : LIGHT_BG);

    useEffect(() => {
        let mounted = true;
        const loadBackground = async () => {
            try {
                const { data } = await api.get("/settings/dashboard");
                if (!mounted) return;
                setBackgroundUrl(data?.background_image_url || (theme === "dark" ? DARK_BG : LIGHT_BG));
            } catch (_err) {
                if (!mounted) return;
                setBackgroundUrl(theme === "dark" ? DARK_BG : LIGHT_BG);
            }
        };
        loadBackground();
        return () => {
            mounted = false;
        };
    }, [theme]);

    return (
        <WebSocketProvider>
            <MeshBackground url={backgroundUrl} />
            <BrowserRouter>
                <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route
                        path="/change-password"
                        element={
                            <ProtectedRoute>
                                <ChangePasswordPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/dashboard"
                        element={
                            <ProtectedRoute>
                                <DashboardPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/discovery"
                        element={
                            <ProtectedRoute adminOnly>
                                <DiscoveryPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/categories"
                        element={
                            <ProtectedRoute adminOnly>
                                <CategoriesPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/users"
                        element={
                            <ProtectedRoute adminOnly>
                                <UsersPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
            </BrowserRouter>
            <Toaster richColors position="top-right" />
        </WebSocketProvider>
    );
}

function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <AppContent />
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
